import React, { useContext } from 'react';
import {
    InputField,
    ResponseError,
    SelectField,
    SubmitButton,
    AsyncSelectField,
    DateField,
} from '@nintendo/forms';
import { Button, Modal } from 'react-bootstrap';
import { FormikProps, Form } from 'formik';
import moment from 'moment-timezone';
import {
    INITIAL_SELECT_ID,
    ACTION_REQUESTED_MAX_LENGTH,
    JUSTIFICATION_MAX_LENGTH,
    DATE_ONLY_FORMAT,
    SERVER_TIMEZONE,
} from '../../../utils/Constants';
import { AmTemplateType, NclDepartment } from '../../../api/amTemplateApiTypes';
import { getNclDepartments, getNclDevCodes } from '../../../api/amTemplateApi';
import { noOptionsHandler } from '../../../utils/displayUtils';
import ConfigContext from '../../../contexts/ConfigContext';
import TextCountedField from '../../common/TextCountedField';
import CurrencyInputField from '../../common/CurrencyInputField';
import useAsyncWithState from '../../../hooks/useAsyncWithState';

export interface UpdateAmModalForm {
    id: number;
    amNumber: number;
    supNumber: number;
    confidential: boolean;
    confidentialFlag: number;
    subject: string;
    actionRequested: string;
    justification: string;
    budgetAmount: number;
    internalCostAmount: number;
    revenueAmount: number;
    completionDate: string;
    nclDepartmentCode: string;
    nclDepartmentDescription: string;
    nclDevCode: string;
    nclDevCodeDescription: string;
}

export interface AmUpdateFormProps extends FormikProps<UpdateAmModalForm> {
    completionDateMoment: moment.Moment;
    maxCompletionDateMoment: moment.Moment;
    template: AmTemplateType;
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

const AmUpdateForm = (props: AmUpdateFormProps) => {
    const config = useContext(ConfigContext);

    const {
        values,
        errors,
        touched,
        isSubmitting,
        dirty,
        responseError,
        setFieldValue,
        toggleModal,
        completionDateMoment,
        maxCompletionDateMoment,
    } = props;

    const { value: nclDepartments } = useAsyncWithState(
        () => getNclDepartments(config),
        [] as NclDepartment[],
    );

    const isDateEnabled = (date: Date) => {
        const dateMoment = moment(date);
        return (
            dateMoment.isSame(completionDateMoment) ||
            (dateMoment.isAfter(moment()) && dateMoment.isSameOrBefore(maxCompletionDateMoment))
        );
    };

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>Edit Action Memo</Modal.Title>
            </Modal.Header>
            <Form>
                <Modal.Body>
                    <ResponseError errorMessage={responseError} />

                    <InputField
                        label="Subject"
                        name="subject"
                        touched={touched.subject}
                        errors={errors.subject}
                        testId="subject"
                        isRequired
                    />

                    <DateField
                        label={`${values.supNumber > 0 ? 'New ' : ''}Completion Date`}
                        name="completionDate"
                        flatpickrProps={{
                            options: {
                                altInputClass: 'form-control',
                                altFormat: DATE_ONLY_FORMAT,
                                enableTime: false,
                                dateFormat: DATE_ONLY_FORMAT,
                                enable: [isDateEnabled],
                                static: true,
                            },
                            value: values.completionDate
                                ? moment(values.completionDate).format(DATE_ONLY_FORMAT)
                                : undefined,
                        }}
                        timezone={SERVER_TIMEZONE}
                        touched={touched.completionDate}
                        errors={errors.completionDate}
                        isRequired
                        testId="completionDate"
                        tooltip={`${
                            values.supNumber > 0
                                ? 'Replaces the completion date when supplement is approved.<br/>'
                                : ''
                        }Maximum completion date allowed is ${maxCompletionDateMoment.format(
                            DATE_ONLY_FORMAT,
                        )}`}
                    />
                    {values.supNumber <= 0 && props.template.showDevCode && (
                        <>
                            <SelectField
                                label="NCL Department"
                                name="nclDepartmentCode"
                                options={
                                    nclDepartments
                                        ? nclDepartments.map((nclDepartment) => ({
                                              id: nclDepartment.code,
                                              name: `[${nclDepartment.code}]${
                                                  nclDepartment.description
                                                      ? ` ${nclDepartment.description}`
                                                      : ''
                                              }`,
                                          }))
                                        : [
                                              {
                                                  id: values.nclDepartmentCode,
                                                  name: `[${values.nclDepartmentCode}]${
                                                      values.nclDepartmentDescription
                                                          ? ` ${values.nclDepartmentDescription}`
                                                          : ''
                                                  }`,
                                              },
                                          ]
                                }
                                touched={touched.nclDepartmentCode}
                                errors={errors.nclDepartmentCode}
                                testId="nclDepartmentCode"
                                isRequired
                            />
                            <AsyncSelectField
                                asyncSelectProps={{
                                    cacheOptions: true,
                                    loadOptions: async (inputValue: string) => {
                                        if (inputValue.length > 2) {
                                            const nclDevCodes = await getNclDevCodes(
                                                config,
                                                inputValue,
                                            );
                                            if (nclDevCodes) {
                                                return nclDevCodes.map((nclDevCode) => ({
                                                    value: nclDevCode.code,
                                                    label: ` [${nclDevCode.code}]${
                                                        nclDevCode.description
                                                            ? ` ${nclDevCode.description}`
                                                            : ''
                                                    }`,
                                                }));
                                            }
                                        }
                                        return [];
                                    },
                                    noOptionsMessage: noOptionsHandler,
                                }}
                                defaultValue={{
                                    value: values.nclDevCode.toString(),
                                    label: ` [${values.nclDevCode}]${
                                        values.nclDevCodeDescription
                                            ? ` ${values.nclDevCodeDescription}`
                                            : ''
                                    }`,
                                }}
                                label="NCL Dev Code"
                                name="nclDevCode"
                                touched={touched.nclDevCode}
                                errors={errors.nclDevCode}
                                testId="nclDevCode"
                                isRequired
                            />
                        </>
                    )}
                    <SelectField
                        label="Confidential"
                        name="confidentialFlag"
                        options={[
                            {
                                id: INITIAL_SELECT_ID,
                                name: 'No',
                            },
                            {
                                id: 1,
                                name: 'Yes',
                            },
                        ]}
                        touched={touched.confidentialFlag}
                        errors={errors.confidentialFlag}
                        testId="confidentialFlag"
                        noDefault
                        isRequired
                        onChange={(id: number) => {
                            if (id) {
                                setFieldValue('confidential', id !== INITIAL_SELECT_ID);
                            }
                        }}
                    />
                    <TextCountedField
                        label="Action Requested"
                        name="actionRequested"
                        touched={touched.actionRequested}
                        errors={errors.actionRequested}
                        isRequired
                        testId="actionRequested"
                        length={values.actionRequested ? values.actionRequested.length : 0}
                        maxLength={ACTION_REQUESTED_MAX_LENGTH}
                        textAreaProps={{
                            placeholder: 'Please provide a short description.',
                            rows: 4,
                        }}
                    />

                    <div className="border-top mb-3" />
                    {(props.template.showBudgetAmount || !!values.budgetAmount) && (
                        <CurrencyInputField
                            contentCssClass=" col-sm-7"
                            label="Budget Amount ($)"
                            name="budgetAmount"
                            touched={touched.budgetAmount}
                            errors={errors.budgetAmount}
                            testId="budgetAmount"
                        />
                    )}
                    {(props.template.showInternalCostAmount || !!values.internalCostAmount) && (
                        <CurrencyInputField
                            contentCssClass="col-sm-7"
                            label="Internal Cost ($)"
                            name="internalCostAmount"
                            touched={touched.internalCostAmount}
                            errors={errors.internalCostAmount}
                            testId="internalCostAmount"
                        />
                    )}
                    {(props.template.showRevenueAmount || !!values.revenueAmount) && (
                        <CurrencyInputField
                            contentCssClass="col-sm-7"
                            label="Expected Revenue ($)"
                            name="revenueAmount"
                            touched={touched.revenueAmount}
                            errors={errors.revenueAmount}
                            testId="revenueAmount"
                        />
                    )}
                    <TextCountedField
                        label="Justification"
                        name="justification"
                        touched={touched.justification}
                        errors={errors.justification}
                        isRequired
                        testId="justification"
                        length={values.justification ? values.justification.length : 0}
                        maxLength={JUSTIFICATION_MAX_LENGTH}
                        textAreaProps={{
                            placeholder:
                                'In addition to your Spend Justification, please indicate if this funds request is included in your current departmental budget.',
                            rows: 4,
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        className="btn-primary ml-auto"
                        disabled={!dirty}
                        isSubmitting={isSubmitting}
                        buttonText="Save"
                    />
                </Modal.Footer>
            </Form>
        </>
    );
};

export default AmUpdateForm;
